/* eslint-disable */
import { useState, useMemo } from "react";
import { saveToStorage, loadFromStorage } from "./Auth";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — LOAD MANAGEMENT
   Flag injured · Recovery · Knocks · Suspended
   Training planner auto-adjusts for unavailable players
   Fixture prep checklist shows availability
   Return-to-play timelines · Injury history log
═══════════════════════════════════════════════════════════ */

const LM_CSS = `
.lm-wrap { font-family:'DM Sans',sans-serif; background:var(--cream); min-height:100%; }

/* HERO */
.lm-hero { background:linear-gradient(135deg,#5a1a1a,var(--darkred)); border-bottom:3px solid var(--mustard); padding:1.5rem 1.8rem; position:relative; overflow:hidden; }
.lm-hero::before { content:''; position:absolute; inset:0; background:repeating-linear-gradient(0deg,transparent,transparent 38px,rgba(255,255,255,0.02) 38px,rgba(255,255,255,0.02) 39px); pointer-events:none; }
.lm-hero-inner { position:relative; z-index:1; display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
.lm-hero-title { font-family:'Bebas Neue',sans-serif; font-size:1.8rem; letter-spacing:3px; color:var(--white); line-height:1; }
.lm-hero-sub { font-size:0.72rem; color:rgba(255,255,255,0.5); font-style:italic; font-weight:300; margin-top:0.2rem; max-width:480px; line-height:1.6; }
.lm-stat-strip { display:flex; gap:0; position:relative; z-index:1; margin-top:1rem; }
.lm-stat { flex:1; text-align:center; padding:0.6rem 0.5rem; border-right:1px solid rgba(255,255,255,0.1); }
.lm-stat:last-child { border-right:none; }
.lm-stat-n { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:2px; line-height:1; }
.lm-stat-n.green { color:#4ade80; }
.lm-stat-n.red { color:#f87171; }
.lm-stat-n.amber { color:var(--mustard); }
.lm-stat-n.white { color:rgba(255,255,255,0.7); }
.lm-stat-l { font-size:0.52rem; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.35); margin-top:0.1rem; }

/* TABS */
.lm-tabs { display:flex; background:var(--white); border-bottom:2px solid var(--rule); overflow-x:auto; }
.lm-tab { padding:0.85rem 1.2rem; font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; color:var(--muted); border-bottom:3px solid transparent; margin-bottom:-2px; white-space:nowrap; transition:all 0.2s; }
.lm-tab:hover { color:var(--teal); }
.lm-tab.on { color:var(--teal); border-bottom-color:var(--teal); }

/* CONTENT */
.lm-content { padding:1.2rem 1.5rem; }
.lm-grid { display:grid; grid-template-columns:1fr 320px; gap:1.2rem; }
.lm-panel { background:var(--white); border:1.5px solid var(--rule); margin-bottom:1.2rem; }
.lm-panel-head { padding:0.85rem 1.1rem; border-bottom:1.5px solid var(--rule); display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:0.5rem; }
.lm-panel-title { font-family:'Bebas Neue',sans-serif; font-size:0.92rem; letter-spacing:2.5px; color:var(--ink); }
.lm-panel-body { padding:1.1rem; }

/* STATUS BADGES */
.lm-status { font-size:0.58rem; font-weight:800; padding:0.12rem 0.48rem; letter-spacing:1.5px; text-transform:uppercase; display:inline-flex; align-items:center; gap:0.3rem; }
.lm-status.available { background:#dcfce7; color:#16a34a; border:1px solid #bbf7d0; }
.lm-status.knock { background:var(--mustard3); color:var(--mustard2); border:1px solid #f5dfa0; }
.lm-status.recovery { background:#dbeafe; color:#1d4ed8; border:1px solid #bfdbfe; }
.lm-status.injured { background:var(--darkred3); color:var(--darkred); border:1px solid #f5b8b8; }
.lm-status.suspended { background:#f3e8ff; color:#7c3aed; border:1px solid #e9d5ff; }

/* LOAD BADGE */
.lm-load { font-size:0.56rem; font-weight:700; padding:0.1rem 0.4rem; letter-spacing:1px; text-transform:uppercase; }
.lm-load.full { background:var(--teal4); color:var(--teal); }
.lm-load.modified { background:var(--mustard3); color:var(--mustard2); }
.lm-load.light { background:#dbeafe; color:#1d4ed8; }
.lm-load.none { background:var(--darkred3); color:var(--darkred); }

/* PLAYER ROWS */
.lm-player-row { display:flex; align-items:center; gap:0.75rem; padding:0.62rem 1rem; border-bottom:1px solid var(--rule2); cursor:pointer; transition:all 0.15s; }
.lm-player-row:last-child { border-bottom:none; }
.lm-player-row:hover { background:var(--cream2); }
.lm-player-row.unavailable { opacity:0.8; }
.lm-player-av { width:32px; height:32px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:0.62rem; font-weight:800; color:white; flex-shrink:0; border:2px solid transparent; }
.lm-player-name { font-size:0.82rem; font-weight:700; color:var(--ink); }
.lm-player-meta { font-size:0.62rem; color:var(--muted); font-weight:300; margin-top:0.05rem; }
.lm-player-days { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:1px; margin-left:auto; flex-shrink:0; }
.lm-edit-btn { padding:0.28rem 0.7rem; background:transparent; border:1.5px solid var(--rule); color:var(--muted); font-family:'DM Sans',sans-serif; font-size:0.62rem; font-weight:800; letter-spacing:1px; text-transform:uppercase; cursor:pointer; transition:all 0.15s; flex-shrink:0; }
.lm-edit-btn:hover { border-color:var(--teal); color:var(--teal); }

/* MODAL */
.lm-modal { position:fixed; inset:0; background:rgba(0,0,0,0.55); z-index:300; display:flex; align-items:center; justify-content:center; padding:1.5rem; }
.lm-modal-card { background:var(--white); border:2.5px solid var(--ink); width:100%; max-width:500px; box-shadow:8px 8px 0 rgba(0,0,0,0.2); max-height:90vh; overflow-y:auto; }
.lm-modal-head { background:var(--darkred); padding:1rem 1.2rem; border-bottom:2px solid var(--mustard); display:flex; align-items:center; justify-content:space-between; }
.lm-modal-title { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:2.5px; color:var(--white); }
.lm-modal-close { background:transparent; border:none; color:rgba(255,255,255,0.6); font-size:1.1rem; cursor:pointer; }
.lm-modal-body { padding:1.2rem; }
.lm-label { display:block; font-size:0.58rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.3rem; margin-top:0.75rem; }
.lm-label:first-child { margin-top:0; }
.lm-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.58rem 0.8rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; cursor:pointer; }
.lm-select:focus { border-bottom-color:var(--teal); }
.lm-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.58rem 0.8rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; }
.lm-input:focus { border-bottom-color:var(--teal); }
.lm-textarea { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.58rem 0.8rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:400; outline:none; resize:vertical; min-height:80px; font-style:italic; }
.lm-modal-actions { display:flex; gap:0.6rem; margin-top:1.2rem; }
.lm-btn-p { flex:1; padding:0.72rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; }
.lm-btn-s { flex:1; padding:0.72rem; background:transparent; color:var(--muted); border:1.5px solid var(--rule); font-family:'DM Sans',sans-serif; font-weight:700; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; }
.lm-btn-danger { flex:1; padding:0.72rem; background:transparent; color:var(--darkred); border:1.5px solid var(--darkred); font-family:'DM Sans',sans-serif; font-weight:700; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; }

/* TRAINING PLANNER ADJUSTMENTS */
.lm-adjust-card { border:1.5px solid var(--rule); padding:0.85rem; margin-bottom:0.65rem; border-left:4px solid; }
.lm-adjust-card.danger { border-left-color:var(--darkred); background:var(--darkred3); }
.lm-adjust-card.warning { border-left-color:var(--mustard); background:var(--mustard3); }
.lm-adjust-card.info { border-left-color:var(--teal); background:var(--teal4); }
.lm-adjust-card.good { border-left-color:#16a34a; background:#dcfce7; }
.lm-adjust-title { font-family:'Bebas Neue',sans-serif; font-size:0.88rem; letter-spacing:1.5px; margin-bottom:0.3rem; }
.lm-adjust-title.danger { color:var(--darkred); }
.lm-adjust-title.warning { color:var(--mustard2); }
.lm-adjust-title.info { color:var(--teal2); }
.lm-adjust-title.good { color:#15803d; }
.lm-adjust-text { font-size:0.75rem; color:var(--ink2); font-weight:400; line-height:1.65; }
.lm-adjust-drill { display:flex; align-items:flex-start; gap:0.45rem; font-size:0.72rem; color:var(--ink2); margin-top:0.35rem; font-weight:500; line-height:1.45; }
.lm-adjust-drill::before { content:'→'; color:var(--teal); font-weight:800; flex-shrink:0; }
.lm-adjust-drill.strike::before { content:'✕'; color:var(--darkred); }
.lm-adjust-drill.strike { text-decoration:line-through; color:var(--muted); }

/* RETURN TIMELINE */
.lm-timeline { position:relative; padding-left:1.5rem; }
.lm-timeline::before { content:''; position:absolute; left:8px; top:0; bottom:0; width:2px; background:var(--rule); }
.lm-timeline-item { position:relative; padding:0 0 1rem 0.75rem; }
.lm-timeline-dot { position:absolute; left:-1.08rem; width:14px; height:14px; border-radius:50%; border:2.5px solid var(--white); top:2px; }
.lm-timeline-date { font-size:0.6rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; color:var(--muted); margin-bottom:0.15rem; }
.lm-timeline-text { font-size:0.78rem; font-weight:600; color:var(--ink); }
.lm-timeline-sub { font-size:0.68rem; color:var(--muted); font-weight:300; margin-top:0.08rem; }

/* FIXTURE AVAILABILITY */
.lm-fixture-row { display:flex; align-items:center; gap:0.75rem; padding:0.6rem 0; border-bottom:1px solid var(--rule2); }
.lm-fixture-row:last-child { border-bottom:none; }
.lm-fixture-date { font-family:'Bebas Neue',sans-serif; font-size:0.85rem; letter-spacing:1px; color:var(--muted); width:55px; flex-shrink:0; }
.lm-fixture-opp { font-size:0.82rem; font-weight:700; color:var(--ink); flex:1; }
.lm-fixture-avail { font-family:'Bebas Neue',sans-serif; font-size:0.9rem; letter-spacing:1px; }
.lm-fixture-warning { font-size:0.62rem; color:var(--darkred); font-weight:600; }
.lm-avail-dots { display:flex; gap:2px; flex-wrap:wrap; max-width:140px; }
.lm-avail-dot { width:8px; height:8px; border-radius:50%; }

/* INJURY HISTORY */
.lm-history-row { display:flex; align-items:flex-start; gap:0.75rem; padding:0.6rem 0; border-bottom:1px solid var(--rule2); }
.lm-history-row:last-child { border-bottom:none; }
.lm-history-name { font-size:0.8rem; font-weight:700; color:var(--ink); }
.lm-history-detail { font-size:0.68rem; color:var(--muted); font-weight:300; margin-top:0.08rem; }
.lm-history-duration { font-family:'Bebas Neue',sans-serif; font-size:0.88rem; letter-spacing:1px; color:var(--darkred); flex-shrink:0; }

/* FILTER ROW */
.lm-filter-row { display:flex; gap:0.4rem; padding:0.75rem 1rem; background:var(--cream2); border-bottom:1.5px solid var(--rule); flex-wrap:wrap; align-items:center; }
.lm-filter-btn { padding:0.3rem 0.75rem; font-family:'DM Sans',sans-serif; font-size:0.62rem; font-weight:800; letter-spacing:1px; text-transform:uppercase; cursor:pointer; border:1.5px solid var(--rule); color:var(--muted); background:transparent; transition:all 0.15s; }
.lm-filter-btn:hover { border-color:var(--teal); color:var(--teal); }
.lm-filter-btn.on { background:var(--teal); border-color:var(--teal); color:var(--white); }

/* EMPTY */
.lm-empty { text-align:center; padding:3rem 2rem; }
.lm-empty-icon { font-size:3rem; margin-bottom:1rem; }
.lm-empty-title { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:2px; margin-bottom:0.5rem; }
.lm-empty-sub { font-size:0.8rem; color:var(--muted); font-style:italic; font-weight:300; max-width:380px; margin:0 auto; line-height:1.7; }
.lm-empty-btn { margin-top:1.2rem; padding:0.72rem 1.8rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; }

@media(max-width:1000px) { .lm-grid{grid-template-columns:1fr;} }
@media(max-width:700px) {
  .lm-content { padding:0.85rem; }
  .lm-hero { padding:1.1rem; }
  .lm-stat-strip { flex-wrap:wrap; }
  .lm-stat { min-width:calc(50% - 1px); }
  .lm-player-row { flex-wrap:wrap; gap:0.4rem; }
  .lm-filter-row { gap:0.3rem; }
  .lm-filter-btn { font-size:0.56rem; padding:0.25rem 0.55rem; }
  .lm-modal { padding:0.5rem; }
}
`;

/* ── DATA ── */
const STATUS_OPTIONS = [
  {id:"available",  label:"✅ Available",          color:"#16a34a", badge:"available"},
  {id:"knock",      label:"⚠️ Carrying a Knock",   color:"var(--mustard)", badge:"knock"},
  {id:"recovery",   label:"🔵 In Recovery",         color:"#1d4ed8", badge:"recovery"},
  {id:"injured",    label:"🔴 Injured — Unavailable",color:"var(--darkred)", badge:"injured"},
  {id:"suspended",  label:"🟣 Suspended",           color:"#7c3aed", badge:"suspended"},
];

const LOAD_LEVELS = [
  {id:"full",     label:"Full Training",     desc:"Normal session — no restrictions"},
  {id:"modified", label:"Modified Training", desc:"No high-intensity contact or sprinting"},
  {id:"light",    label:"Light Training",    desc:"Technical work only — no physical load"},
  {id:"rehab",    label:"Rehab Only",        desc:"Individual physio programme"},
  {id:"none",     label:"No Training",       desc:"Complete rest — match day excluded"},
];

const INJURY_TYPES = [
  "Hamstring strain","Quad strain","Calf strain","Groin strain",
  "Ankle sprain","Knee ligament","Knee meniscus","Thigh bruise",
  "Back problem","Shoulder injury","Concussion","Illness",
  "Fatigue","Muscle fatigue","Hip flexor","Shin splints","Foot injury","Other",
];

/* ── TRAINING ADJUSTMENTS ENGINE ── */
function generateTrainingAdjustments(squad, loadData) {
  const byStatus = {
    injured:   squad.filter(p=>loadData[p.id]?.status==="injured"),
    knock:     squad.filter(p=>loadData[p.id]?.status==="knock"),
    recovery:  squad.filter(p=>loadData[p.id]?.status==="recovery"),
    suspended: squad.filter(p=>loadData[p.id]?.status==="suspended"),
  };

  const adjustments = [];

  // Full squad available
  if(Object.values(byStatus).every(arr=>arr.length===0)) {
    adjustments.push({
      type:"good",
      title:"Full Squad Available",
      text:"No load management required this session. All players can participate in full training.",
      drills:[],
    });
    return adjustments;
  }

  // Injured players
  if(byStatus.injured.length>0) {
    const names = byStatus.injured.map(p=>p.name.split(" ")[0]).join(", ");
    const positions = [...new Set(byStatus.injured.map(p=>p.pos))];
    adjustments.push({
      type:"danger",
      title:`${byStatus.injured.length} Player${byStatus.injured.length>1?"s":""} Unavailable`,
      text:`${names} must not train or play. Adjust your tactical work to account for missing ${positions.join(", ")} position${positions.length>1?"s":""}. Consider promoting from development squad or using a positional hybrid.`,
      drills:[
        {text:"Remove any contact drills that require these positions", strike:true},
        {text:`Adjust formation work to cover missing ${positions.join("/")} slot${positions.length>1?"s":""}`},
        {text:"Use a training mannequin or positional shadow to maintain shape patterns"},
      ],
    });
  }

  // Players with knocks
  if(byStatus.knock.length>0) {
    const names = byStatus.knock.map(p=>p.name.split(" ")[0]).join(", ");
    const injTypes = byStatus.knock.map(p=>loadData[p.id]?.injuryType).filter(Boolean);
    adjustments.push({
      type:"warning",
      title:`${byStatus.knock.length} Player${byStatus.knock.length>1?"s":""} on Modified Load`,
      text:`${names} can train but with restrictions. No high-intensity sprinting, sharp turns or physical contact. ${injTypes.length>0?`Watch specifically: ${injTypes.join(", ")}.`:""}`,
      drills:[
        {text:"High-intensity sprint drills — exclude these players", strike:true},
        {text:"Contact-based pressing drills — use modified version without physical challenges", strike:true},
        {text:"Technical ball-work and positioning patterns — these players can participate fully"},
        {text:"Set piece rehearsal — low risk, include in routines"},
      ],
    });
  }

  // Recovery players
  if(byStatus.recovery.length>0) {
    const names = byStatus.recovery.map(p=>p.name.split(" ")[0]).join(", ");
    const returnDates = byStatus.recovery.map(p=>{
      const d = loadData[p.id]?.returnDate;
      if(!d) return null;
      const days = Math.ceil((new Date(d)-new Date())/(1000*60*60*24));
      return days>=0?`${p.name.split(" ")[0]} back in ${days}d`:null;
    }).filter(Boolean);
    adjustments.push({
      type:"info",
      title:`${byStatus.recovery.length} Player${byStatus.recovery.length>1?"s":""} in Recovery Protocol`,
      text:`${names} should do light technical work only — no running at pace or physical challenge. ${returnDates.length>0?returnDates.join(" · ")+".":" Monitor daily and reassess."}`,
      drills:[
        {text:"Full-team press drills — exclude recovery players", strike:true},
        {text:"Light possession rondo (small area, slow tempo) — recovery players can join"},
        {text:"Passing patterns at walking pace — technical focus only"},
        {text:"Consider individual 1-on-1 work with physio during main session"},
      ],
    });
  }

  // Suspended players
  if(byStatus.suspended.length>0) {
    const names = byStatus.suspended.map(p=>p.name.split(" ")[0]).join(", ");
    adjustments.push({
      type:"info",
      title:`${byStatus.suspended.length} Player${byStatus.suspended.length>1?"s":""} Suspended`,
      text:`${names} cannot feature in the next match. They can train fully but plan your matchday tactics without them.`,
      drills:[
        {text:"Include in all training drills — no physical restrictions"},
        {text:"Plan your matchday XI and set pieces excluding the suspended player(s)"},
        {text:"Use this session to rehearse formations with their replacement"},
      ],
    });
  }

  return adjustments;
}

/* ── HELPERS ── */
function posGroup(pos) {
  if(pos==="GK") return "gk";
  if(["CB","RB","LB","WB","RWB","LWB"].includes(pos)) return "def";
  if(["CDM","CM","CAM","RM","LM"].includes(pos)) return "mid";
  return "att";
}
function posColor(g) {
  return g==="gk"?"#b45309":g==="def"?"var(--darkred)":g==="mid"?"var(--teal)":"#16a34a";
}
function ini(n) { return (n||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2); }
function daysUntil(dateStr) {
  if(!dateStr) return null;
  const d = Math.ceil((new Date(dateStr)-new Date())/(1000*60*60*24));
  return d;
}
function fmtDate(d) { return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short"}); }

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
export default function LoadManagement({ squad=[], onNavigate }) {
  const [activeTab, setActiveTab] = useState("squad");
  const [loadData, setLoadData] = useState(()=>loadFromStorage("load_management",{})||{});
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editForm, setEditForm] = useState({status:"available",load:"full",injuryType:"",returnDate:"",notes:""});
  const [statusFilter, setStatusFilter] = useState("All");
  const [injuryHistory] = useState(()=>loadFromStorage("injury_history",[])||[]);

  // Load fixtures for availability forecast
  const fixtures = useMemo(()=>loadFromStorage("fx_fixtures",[])||[],[]);
  const upcoming = useMemo(()=>[...fixtures.filter(f=>!f.played)].sort((a,b)=>new Date(a.date)-new Date(b.date)),[fixtures]);

  /* ── SAVE ── */
  const saveLoad = (updated) => {
    setLoadData(updated);
    saveToStorage("load_management", updated);
    // Also save to player availability in squad
    const squad_updated = (loadFromStorage("my_squad",[])||[]).map(p => {
      const d = updated[p.id];
      if(!d) return p;
      const statusMap = {available:"Available",knock:"Doubt",recovery:"Doubt",injured:"Injured",suspended:"Suspended"};
      return {...p, availability: statusMap[d.status]||"Available"};
    });
    saveToStorage("my_squad", squad_updated);
  };

  /* ── EDIT MODAL ── */
  const openEdit = (player) => {
    const data = loadData[player.id]||{status:"available",load:"full",injuryType:"",returnDate:"",notes:""};
    setEditForm({...data});
    setEditingPlayer(player);
  };

  const saveEdit = () => {
    if(!editingPlayer) return;
    const updated = {...loadData, [editingPlayer.id]: {
      ...editForm,
      updatedAt: new Date().toISOString(),
      playerName: editingPlayer.name,
      pos: editingPlayer.pos,
    }};
    saveLoad(updated);
    setEditingPlayer(null);
  };

  const clearStatus = (playerId) => {
    const updated = {...loadData};
    delete updated[playerId];
    saveLoad(updated);
  };

  /* ── DERIVED DATA ── */
  const getStatus = (id) => loadData[id]?.status||"available";
  const getLoad   = (id) => loadData[id]?.load||"full";

  const available  = squad.filter(p=>getStatus(p.id)==="available").length;
  const injured    = squad.filter(p=>getStatus(p.id)==="injured").length;
  const knocks     = squad.filter(p=>getStatus(p.id)==="knock").length;
  const recovery   = squad.filter(p=>getStatus(p.id)==="recovery").length;
  const suspended  = squad.filter(p=>getStatus(p.id)==="suspended").length;
  const unavailable = injured+suspended;

  const filteredSquad = useMemo(()=>{
    if(statusFilter==="All") return squad;
    return squad.filter(p=>getStatus(p.id)===statusFilter);
  },[squad,statusFilter,loadData]);

  // Training adjustments
  const adjustments = useMemo(()=>generateTrainingAdjustments(squad,loadData),[squad,loadData]);

  // Return-to-play timeline
  const returningPlayers = squad.filter(p=>{
    const d = loadData[p.id];
    return d && d.status!=="available" && d.returnDate;
  }).sort((a,b)=>new Date(loadData[a.id].returnDate)-new Date(loadData[b.id].returnDate));

  // Fixture availability forecast
  const fixtureForecast = upcoming.slice(0,5).map(f=>{
    const days = daysUntil(f.date);
    // Players who might be back by match date
    const unavail = squad.filter(p=>{
      const d = loadData[p.id];
      if(!d||d.status==="available") return false;
      if(!d.returnDate) return d.status==="injured"||d.status==="suspended";
      return daysUntil(d.returnDate)>days;
    });
    return {...f, unavailableCount:unavail.length, unavailablePlayers:unavail};
  });

  const TABS = [
    {id:"squad",    label:"Squad Availability"},
    {id:"training", label:"Session Adjustments"},
    {id:"fixtures", label:"Fixture Forecast"},
    {id:"timeline", label:"Return to Play"},
  ];

  return (
    <>
      <style>{LM_CSS}</style>
      <div className="lm-wrap">

        {/* EDIT MODAL */}
        {editingPlayer&&(
          <div className="lm-modal" onClick={()=>setEditingPlayer(null)}>
            <div className="lm-modal-card" onClick={e=>e.stopPropagation()}>
              <div className="lm-modal-head">
                <div className="lm-modal-title">{editingPlayer.name} — Load Status</div>
                <button className="lm-modal-close" onClick={()=>setEditingPlayer(null)}>✕</button>
              </div>
              <div className="lm-modal-body">
                <label className="lm-label">Current Status *</label>
                <select className="lm-select" value={editForm.status} onChange={e=>setEditForm(p=>({...p,status:e.target.value}))}>
                  {STATUS_OPTIONS.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                </select>

                <label className="lm-label">Training Load</label>
                <select className="lm-select" value={editForm.load} onChange={e=>setEditForm(p=>({...p,load:e.target.value}))}>
                  {LOAD_LEVELS.map(l=><option key={l.id} value={l.id}>{l.label} — {l.desc}</option>)}
                </select>

                {(editForm.status==="injured"||editForm.status==="knock")&&(
                  <>
                    <label className="lm-label">Injury / Condition</label>
                    <select className="lm-select" value={editForm.injuryType} onChange={e=>setEditForm(p=>({...p,injuryType:e.target.value}))}>
                      <option value="">— Select type —</option>
                      {INJURY_TYPES.map(t=><option key={t}>{t}</option>)}
                    </select>
                  </>
                )}

                {editForm.status!=="available"&&(
                  <>
                    <label className="lm-label">Expected Return Date</label>
                    <input className="lm-input" type="date" value={editForm.returnDate||""} onChange={e=>setEditForm(p=>({...p,returnDate:e.target.value}))}/>
                    {editForm.returnDate&&(
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.88rem",letterSpacing:"1px",color:daysUntil(editForm.returnDate)<=7?"var(--darkred)":"var(--teal)",marginTop:"0.35rem"}}>
                        {daysUntil(editForm.returnDate)>=0?`Returns in ${daysUntil(editForm.returnDate)} days`:"Return date has passed"}
                      </div>
                    )}
                  </>
                )}

                <label className="lm-label">Notes for Physio / Coaching Staff</label>
                <textarea className="lm-textarea" placeholder="Details of injury, symptoms, treatment plan, restrictions..." value={editForm.notes||""} onChange={e=>setEditForm(p=>({...p,notes:e.target.value}))}/>

                <div className="lm-modal-actions">
                  <button className="lm-btn-s" onClick={()=>setEditingPlayer(null)}>Cancel</button>
                  {loadData[editingPlayer.id]&&(
                    <button className="lm-btn-danger" onClick={()=>{clearStatus(editingPlayer.id);setEditingPlayer(null);}}>Clear Status</button>
                  )}
                  <button className="lm-btn-p" onClick={saveEdit}>Save Status</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* HERO */}
        <div className="lm-hero">
          <div className="lm-hero-inner">
            <div>
              <div className="lm-hero-title">Load Management</div>
              <div className="lm-hero-sub">Flag injured players, manage training loads and see how availability affects upcoming fixtures. The training planner adjusts automatically.</div>
            </div>
            {squad.length>0&&(
              <div style={{display:"flex",gap:"0.5rem",alignSelf:"flex-start"}}>
                <button onClick={()=>setActiveTab("training")} style={{padding:"0.45rem 0.9rem",background:"rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.8)",border:"1px solid rgba(255,255,255,0.2)",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.65rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>
                  Session Adjustments →
                </button>
              </div>
            )}
          </div>
          <div className="lm-stat-strip">
            <div className="lm-stat"><div className="lm-stat-n green">{available}</div><div className="lm-stat-l">Available</div></div>
            <div className="lm-stat"><div className="lm-stat-n amber">{knocks+recovery}</div><div className="lm-stat-l">Modified Load</div></div>
            <div className="lm-stat"><div className="lm-stat-n red">{injured}</div><div className="lm-stat-l">Injured</div></div>
            <div className="lm-stat"><div className="lm-stat-n white">{suspended}</div><div className="lm-stat-l">Suspended</div></div>
            <div className="lm-stat"><div className="lm-stat-n white">{returningPlayers.length}</div><div className="lm-stat-l">Return Soon</div></div>
          </div>
        </div>

        {/* TABS */}
        <div className="lm-tabs">
          {TABS.map(t=>(
            <div key={t.id} className={`lm-tab ${activeTab===t.id?"on":""}`} onClick={()=>setActiveTab(t.id)}>{t.label}</div>
          ))}
        </div>

        <div className="lm-content">

          {/* ══ SQUAD AVAILABILITY ══ */}
          {activeTab==="squad"&&(
            squad.length===0?(
              <div className="lm-empty">
                <div className="lm-empty-icon">🏥</div>
                <div className="lm-empty-title">No Squad Built</div>
                <div className="lm-empty-sub">Add players to your squad first. Then come back here to manage their availability, flag injuries and plan training around who's fit.</div>
                <button className="lm-empty-btn" onClick={()=>onNavigate&&onNavigate("squadcoach")}>Build Squad →</button>
              </div>
            ):(
              <div className="lm-panel">
                {/* Filters */}
                <div className="lm-filter-row">
                  {["All","available","knock","recovery","injured","suspended"].map(f=>(
                    <button key={f} className={`lm-filter-btn ${statusFilter===f?"on":""}`} onClick={()=>setStatusFilter(f)}>
                      {f==="All"?"All Players":STATUS_OPTIONS.find(s=>s.id===f)?.label||f}
                    </button>
                  ))}
                  <div style={{marginLeft:"auto",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic"}}>
                    Click any player to update their status
                  </div>
                </div>
                {/* Player rows */}
                {filteredSquad.map(p=>{
                  const status = getStatus(p.id);
                  const load   = getLoad(p.id);
                  const data   = loadData[p.id]||{};
                  const grp    = p.group||posGroup(p.pos);
                  const days   = data.returnDate?daysUntil(data.returnDate):null;
                  return(
                    <div key={p.id} className={`lm-player-row ${status!=="available"?"unavailable":""}`} onClick={()=>openEdit(p)}>
                      <div className="lm-player-av" style={{background:posColor(grp),borderColor:status==="injured"?"var(--darkred)":status==="knock"?"var(--mustard)":status==="recovery"?"#1d4ed8":status==="suspended"?"#7c3aed":"transparent"}}>
                        {ini(p.name)}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div className="lm-player-name">{p.name}</div>
                        <div className="lm-player-meta">
                          {p.pos} · Age {p.age||"—"}
                          {data.injuryType&&<span style={{marginLeft:"0.4rem",fontStyle:"italic"}}>· {data.injuryType}</span>}
                        </div>
                      </div>
                      <span className={`lm-status ${status}`}>
                        {STATUS_OPTIONS.find(s=>s.id===status)?.label.split(" ").slice(1).join(" ")||status}
                      </span>
                      <span className={`lm-load ${load}`}>{LOAD_LEVELS.find(l=>l.id===load)?.label||load}</span>
                      {days!==null&&(
                        <div className="lm-player-days" style={{color:days<=3?"var(--darkred)":days<=7?"var(--mustard)":"var(--muted)"}}>
                          {days>=0?`${days}d`:``}
                        </div>
                      )}
                      <button className="lm-edit-btn" onClick={e=>{e.stopPropagation();openEdit(p);}}>Edit</button>
                    </div>
                  );
                })}
                {filteredSquad.length===0&&(
                  <div style={{textAlign:"center",padding:"2rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--muted)",fontStyle:"italic"}}>
                    No players with this status
                  </div>
                )}
              </div>
            )
          )}

          {/* ══ SESSION ADJUSTMENTS ══ */}
          {activeTab==="training"&&(
            <>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--muted)",fontStyle:"italic",marginBottom:"1rem",lineHeight:1.7}}>
                These adjustments are automatically generated from your current squad availability. Apply them to your next training session to protect injured players and get the most from those who are fit.
              </div>
              {adjustments.map((adj,i)=>(
                <div key={i} className={`lm-adjust-card ${adj.type}`}>
                  <div className={`lm-adjust-title ${adj.type}`}>{adj.title}</div>
                  <div className="lm-adjust-text">{adj.text}</div>
                  {adj.drills.length>0&&(
                    <div style={{marginTop:"0.6rem"}}>
                      {adj.drills.map((d,j)=>(
                        <div key={j} className={`lm-adjust-drill ${d.strike?"strike":""}`}>{d.text}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Recommended session intensity */}
              {squad.length>0&&(
                <div className="lm-panel">
                  <div className="lm-panel-head">
                    <div className="lm-panel-title">Recommended Session Intensity</div>
                  </div>
                  <div className="lm-panel-body">
                    {(()=>{
                      const ratio = unavailable/Math.max(squad.length,1);
                      const intensity = ratio>=0.3?"Low — significant absences reduce training options":ratio>=0.15?"Medium — adapt key drills for missing players":"High — squad largely available, full session possible";
                      const color = ratio>=0.3?"var(--darkred)":ratio>=0.15?"var(--mustard)":"#16a34a";
                      return(
                        <>
                          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.2rem",letterSpacing:"2px",color,marginBottom:"0.5rem"}}>{intensity.split(" — ")[0]}</div>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--ink2)",fontWeight:300,lineHeight:1.65,marginBottom:"0.85rem"}}>{intensity.split(" — ")[1]}</div>
                          <div style={{height:10,background:"var(--cream2)",borderRadius:5,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${Math.round((1-ratio)*100)}%`,background:color,borderRadius:5,transition:"width 1s ease"}}/>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",marginTop:"0.35rem"}}>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--muted)",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase"}}>{available} available</div>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--darkred)",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase"}}>{unavailable} unavailable</div>
                          </div>
                        </>
                      );
                    })()}
                    <div style={{marginTop:"1rem",borderTop:"1px solid var(--rule)",paddingTop:"0.85rem"}}>
                      <button onClick={()=>onNavigate&&onNavigate("squadcoach")} style={{padding:"0.52rem 1.1rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.68rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>
                        Open Training Planner →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ══ FIXTURE FORECAST ══ */}
          {activeTab==="fixtures"&&(
            upcoming.length===0?(
              <div className="lm-empty">
                <div className="lm-empty-icon">📅</div>
                <div className="lm-empty-title">No Upcoming Fixtures</div>
                <div className="lm-empty-sub">Add fixtures to see how your current injury list affects each upcoming match.</div>
                <button className="lm-empty-btn" onClick={()=>onNavigate&&onNavigate("fixtures")}>Add Fixtures →</button>
              </div>
            ):(
              <>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--muted)",fontStyle:"italic",marginBottom:"1rem",lineHeight:1.7}}>
                  Projected squad availability for upcoming matches — based on current statuses and expected return dates. Players marked with no return date are assumed unavailable until manually cleared.
                </div>
                <div className="lm-panel">
                  <div className="lm-panel-head">
                    <div className="lm-panel-title">Upcoming Match Availability</div>
                    <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,padding:"0.12rem 0.45rem",background:"var(--teal)",color:"var(--white)",letterSpacing:"2px",textTransform:"uppercase"}}>{upcoming.length} fixtures</span>
                  </div>
                  <div className="lm-panel-body">
                    {fixtureForecast.map((f,i)=>{
                      const days = daysUntil(f.date);
                      const totalAvail = squad.length-f.unavailableCount;
                      const pct = squad.length>0?Math.round((totalAvail/squad.length)*100):100;
                      return(
                        <div key={i} style={{padding:"0.85rem 0",borderBottom:"1.5px solid var(--rule2)"}}>
                          <div className="lm-fixture-row" style={{padding:0,borderBottom:"none"}}>
                            <div className="lm-fixture-date">{fmtDate(f.date)}</div>
                            <div>
                              <div className="lm-fixture-opp">vs {f.opponent}</div>
                              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",marginTop:"0.05rem"}}>
                                {f.competition} · {f.venue==="home"?"Home":"Away"} · {days}d away
                              </div>
                            </div>
                            <div style={{marginLeft:"auto",textAlign:"right",flexShrink:0}}>
                              <div className="lm-fixture-avail" style={{color:pct>=90?"#16a34a":pct>=75?"var(--mustard)":"var(--darkred)"}}>{totalAvail}/{squad.length}</div>
                              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--muted)",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase"}}>available</div>
                            </div>
                          </div>
                          {/* Availability bar */}
                          <div style={{marginTop:"0.5rem",height:7,background:"var(--cream2)",borderRadius:3,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${pct}%`,background:pct>=90?"#16a34a":pct>=75?"var(--mustard)":"var(--darkred)",borderRadius:3}}/>
                          </div>
                          {/* Unavailable players */}
                          {f.unavailablePlayers.length>0&&(
                            <div style={{marginTop:"0.45rem",display:"flex",gap:"0.3rem",flexWrap:"wrap"}}>
                              {f.unavailablePlayers.map(p=>(
                                <span key={p.id} style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:700,padding:"0.06rem 0.4rem",background:"var(--darkred3)",color:"var(--darkred)",border:"1px solid #f5b8b8"}}>
                                  {p.name.split(" ")[0]} ({p.pos})
                                </span>
                              ))}
                              <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)",fontStyle:"italic",padding:"0.06rem 0",alignSelf:"center"}}>may miss this fixture</span>
                            </div>
                          )}
                          {f.unavailableCount===0&&(
                            <div style={{marginTop:"0.35rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"#16a34a",fontWeight:700}}>✓ Full squad projected available</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )
          )}

          {/* ══ RETURN TO PLAY ══ */}
          {activeTab==="timeline"&&(
            returningPlayers.length===0?(
              <div className="lm-empty">
                <div className="lm-empty-icon">🏃</div>
                <div className="lm-empty-title">No Return Dates Set</div>
                <div className="lm-empty-sub">When you flag a player as injured or in recovery, set their expected return date. It will appear here as a timeline so you can plan around their absence.</div>
              </div>
            ):(
              <>
                <div className="lm-panel" style={{marginBottom:"1.2rem"}}>
                  <div className="lm-panel-head">
                    <div className="lm-panel-title">Return to Play Timeline</div>
                    <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,padding:"0.12rem 0.45rem",background:"var(--darkred)",color:"var(--white)",letterSpacing:"2px",textTransform:"uppercase"}}>{returningPlayers.length} players</span>
                  </div>
                  <div className="lm-panel-body">
                    <div className="lm-timeline">
                      {returningPlayers.map((p,i)=>{
                        const data = loadData[p.id];
                        const days = daysUntil(data.returnDate);
                        const status = STATUS_OPTIONS.find(s=>s.id===data.status);
                        const color = data.status==="injured"?"var(--darkred)":data.status==="knock"?"var(--mustard)":"#1d4ed8";
                        const grp = p.group||posGroup(p.pos);
                        return(
                          <div key={p.id} className="lm-timeline-item">
                            <div className="lm-timeline-dot" style={{background:color}}/>
                            <div className="lm-timeline-date">{fmtDate(data.returnDate)} · {days>=0?`${days} days`:days===-0?"Today":"Overdue"}</div>
                            <div className="lm-timeline-text">{p.name} <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",fontWeight:400,color:"var(--muted)",marginLeft:"0.3rem"}}>({p.pos})</span></div>
                            <div className="lm-timeline-sub">
                              <span className={`lm-status ${data.status}`} style={{marginRight:"0.4rem"}}>{status?.label.split(" ").slice(1).join(" ")}</span>
                              {data.injuryType&&<span>{data.injuryType}</span>}
                              {data.load&&<span style={{marginLeft:"0.4rem"}}>· {LOAD_LEVELS.find(l=>l.id===data.load)?.label}</span>}
                            </div>
                            {data.notes&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.25rem",lineHeight:1.55}}>{data.notes}</div>}
                            {/* Next upcoming fixture before return */}
                            {days>0&&(()=>{
                              const missedFixtures = upcoming.filter(f=>daysUntil(f.date)<days).slice(0,2);
                              return missedFixtures.length>0?(
                                <div style={{marginTop:"0.3rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--darkred)",fontWeight:600}}>
                                  Likely to miss: {missedFixtures.map(f=>`vs ${f.opponent} (${fmtDate(f.date)})`).join(", ")}
                                </div>
                              ):null;
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* All statuses summary */}
                <div className="lm-panel">
                  <div className="lm-panel-head"><div className="lm-panel-title">Full Injury List</div></div>
                  <div className="lm-panel-body">
                    {squad.filter(p=>loadData[p.id]&&loadData[p.id].status!=="available").map((p,i)=>{
                      const data = loadData[p.id];
                      return(
                        <div key={p.id} className="lm-history-row">
                          <div style={{flex:1,minWidth:0}}>
                            <div className="lm-history-name">{p.name} <span style={{fontFamily:"'DM Sans',sans-serif",fontWeight:400,fontSize:"0.7rem",color:"var(--muted)"}}>{p.pos}</span></div>
                            <div className="lm-history-detail">
                              {data.injuryType||"No injury specified"}
                              {data.notes&&<span style={{marginLeft:"0.4rem"}}>· {data.notes.slice(0,60)}{data.notes.length>60?"...":""}</span>}
                            </div>
                          </div>
                          <span className={`lm-status ${data.status}`}>{STATUS_OPTIONS.find(s=>s.id===data.status)?.label.split(" ").slice(1).join(" ")}</span>
                          {data.returnDate&&(
                            <div className="lm-history-duration">
                              {daysUntil(data.returnDate)>=0?`${daysUntil(data.returnDate)}d`:"Due back"}
                            </div>
                          )}
                          <button className="lm-edit-btn" onClick={()=>openEdit(p)}>Edit</button>
                        </div>
                      );
                    })}
                    {squad.filter(p=>loadData[p.id]&&loadData[p.id].status!=="available").length===0&&(
                      <div style={{textAlign:"center",padding:"1.5rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--muted)",fontStyle:"italic"}}>No active injuries or restrictions</div>
                    )}
                  </div>
                </div>
              </>
            )
          )}

        </div>
      </div>
    </>
  );
}
